/**
 * AskBlipPanel — the Ask BLIP slide-out, available on every staff page.
 *
 * It tells the server which page the user is on, so "this run" / "this
 * audit" resolve, and offers starter questions that fit that page. The open
 * conversation is remembered per browser (a convenience only — the
 * conversation itself lives on the server).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { AssistantPageContext } from '@aurora/shared';
import { AssistantThread } from './AssistantThread';

const STORE_KEY = 'aurora.askblip.conversation';

function readStored(): string | null {
  try {
    return localStorage.getItem(STORE_KEY);
  } catch {
    return null;
  }
}
function writeStored(id: string | null): void {
  try {
    if (id) localStorage.setItem(STORE_KEY, id);
    else localStorage.removeItem(STORE_KEY);
  } catch {
    /* private window — fine */
  }
}

const UUID = '[0-9a-fA-F-]{36}';

/** Map the current route to the context the assistant understands. */
export function pageContext(pathname: string): AssistantPageContext {
  const m = (re: string) => new RegExp(`^${re}$`).exec(pathname);
  let r;
  if ((r = m(`/benchmarks/(${UUID})`)))
    return { route: pathname, kind: 'benchmark', id: r[1] };
  if ((r = m(`/content-audits/(${UUID})`)))
    return { route: pathname, kind: 'content-audit', id: r[1] };
  if ((r = m(`/selection-audits/(${UUID})`)))
    return { route: pathname, kind: 'selection-audit', id: r[1] };
  if (pathname.startsWith('/suppliers'))
    return { route: pathname, kind: 'suppliers' };
  if (pathname.startsWith('/sources'))
    return { route: pathname, kind: 'sources' };
  return { route: pathname, kind: 'other' };
}

export function startersFor(page: AssistantPageContext): string[] {
  switch (page.kind) {
    case 'benchmark':
      return [
        "Explain this report's headline in plain English",
        'Who is ahead of this company at each buyer-journey stage, and why?',
        'Which sources do the AI models cite most in this run?',
        'What should I tell the client first?',
      ];
    case 'content-audit':
    case 'selection-audit':
      return [
        'Explain these scores in plain English',
        'What is the biggest thing holding this company back?',
      ];
    case 'suppliers':
      return [
        'How does BLIP decide which company a name belongs to?',
        'Which suppliers are waiting for review?',
      ];
    case 'sources':
      return [
        'How is a cited website given its Source Category?',
        'Which domains are still Unclassified most often?',
      ];
    default:
      return [
        'How is share of voice calculated?',
        'List the benchmark runs from the last month',
        'Which source types do AI models cite most at each buyer-journey stage?',
        'Make me a short PDF brief on citation sources in Antibodies',
      ];
  }
}

export function AskBlipPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  const location = useLocation();
  const [conversationId, setConversationId] = useState<string | null>(
    readStored,
  );
  const page = useMemo(
    () => pageContext(location.pathname),
    [location.pathname],
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <aside className="ab-panel" aria-label="Ask BLIP">
      <header className="ab-panel-head">
        <div>
          <strong>✦ Ask BLIP</strong>
          <span className="muted small"> · read-only assistant</span>
        </div>
        <div className="ab-panel-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setConversationId(null);
              writeStored(null);
            }}
          >
            New chat
          </button>
          <Link
            className="btn btn-ghost"
            to={
              conversationId ? `/assistant?c=${conversationId}` : '/assistant'
            }
            onClick={onClose}
          >
            History
          </Link>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      </header>
      <AssistantThread
        conversationId={conversationId}
        onConversation={(id) => {
          setConversationId(id);
          writeStored(id);
        }}
        page={page}
        starters={startersFor(page)}
      />
    </aside>
  );
}
